// src/contexts/ModuloContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { AREAS, temArea } from "../utils/permissoes";

const ModuloContext = createContext(null);

const STORAGE_KEY = "mamute.activeArea";

function ordenarAreas(areas) {
  // ordem preferida no seletor
  const ordem = [AREAS.MANUTENCAO, AREAS.FINANCEIRO];
  return [...areas].sort((a, b) => ordem.indexOf(a) - ordem.indexOf(b));
}

export function ModuloProvider({ children }) {
  const { usuario } = useAuth();
  const [activeArea, setActiveArea] = useState(null);

  const allowedAreas = useMemo(() => {
    if (!usuario) return [];
    const all = [AREAS.MANUTENCAO, AREAS.FINANCEIRO];
    const permitidas = all.filter((a) => temArea(usuario, a));
    return ordenarAreas(permitidas);
  }, [usuario]);

  useEffect(() => {
    if (!usuario) {
      setActiveArea(null);
      return;
    }

    // Se só tem 1 área permitida, trava nela
    if (allowedAreas.length === 1) {
      setActiveArea(allowedAreas[0]);
      try {
        localStorage.setItem(STORAGE_KEY, allowedAreas[0]);
      } catch {}
      return;
    }

    // Tenta recuperar do storage
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {}

    const normalizedStored = stored === "FERRAMENTARIA" ? AREAS.FINANCEIRO : stored;
    if (normalizedStored && allowedAreas.includes(normalizedStored)) {
      setActiveArea(normalizedStored);
      return;
    }

    // Tenta defaultModule/defaultArea no usuário (opcional)
    const preferida = String(usuario?.defaultModule || usuario?.defaultArea || "").toUpperCase();
    const preferidaNormalizada = preferida === "FERRAMENTARIA" ? AREAS.FINANCEIRO : preferida;
    if (preferidaNormalizada && allowedAreas.includes(preferidaNormalizada)) {
      setActiveArea(preferidaNormalizada);
      try { localStorage.setItem(STORAGE_KEY, preferidaNormalizada); } catch {}
      return;
    }

    // Fallback: primeira permitida
    const fallback = allowedAreas[0] || AREAS.MANUTENCAO;
    setActiveArea(fallback);
    try { localStorage.setItem(STORAGE_KEY, fallback); } catch {}
  }, [usuario, allowedAreas]);

  const value = useMemo(() => {
    const trocarArea = (nova) => {
      const a = String(nova || "").toUpperCase();
      if (!a) return;
      if (!allowedAreas.includes(a)) return;
      setActiveArea(a);
      try { localStorage.setItem(STORAGE_KEY, a); } catch {}
    };

    return {
      allowedAreas,
      activeArea: activeArea || (allowedAreas.length === 1 ? allowedAreas[0] : null),
      setActiveArea: trocarArea,
    };
  }, [allowedAreas, activeArea]);

  return <ModuloContext.Provider value={value}>{children}</ModuloContext.Provider>;
}

export function useModulo() {
  const ctx = useContext(ModuloContext);
  if (!ctx) {
    return { allowedAreas: [], activeArea: null, setActiveArea: () => {} };
  }
  return ctx;
}
